import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const PILL_COLORS = [
  { bg: 'rgba(83,74,183,0.08)',   border: 'rgba(83,74,183,0.22)',  text: '#534AB7' },
  { bg: 'rgba(34,211,238,0.08)',  border: 'rgba(34,211,238,0.22)', text: '#06B6D4' },
  { bg: 'rgba(52,211,153,0.08)',  border: 'rgba(52,211,153,0.22)', text: '#10B981' },
  { bg: 'rgba(251,146,60,0.1)',   border: 'rgba(251,146,60,0.25)', text: '#F97316' },
  { bg: 'rgba(232,121,249,0.08)', border: 'rgba(232,121,249,0.22)',text: '#D946EF' },
  { bg: 'rgba(250,204,21,0.08)',  border: 'rgba(250,204,21,0.22)', text: '#CA8A04' },
  { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.22)',text: '#EF4444' },
  { bg: 'rgba(56,189,248,0.08)',  border: 'rgba(56,189,248,0.22)', text: '#0284C7' },
  { bg: 'rgba(168,85,247,0.08)',  border: 'rgba(168,85,247,0.22)', text: '#7C3AED' },
];

function useCourseColors() {
  const map = useRef({});
  const idx = useRef(0);
  return (code) => {
    if (!code) return PILL_COLORS[0];
    const prefix = code.replace(/[0-9\s]/g, '').slice(0, 3).toUpperCase();
    if (!map.current[prefix]) {
      map.current[prefix] = PILL_COLORS[idx.current % PILL_COLORS.length];
      idx.current++;
    }
    return map.current[prefix];
  };
}

// ── Sort time slots using start_time from DB (HH:MM:SS string) ──────────────
// Each entry has start_time e.g. "08:00:00". We build an ordered unique list
// of labels by sorting on that field, then deduplicate by label.
function buildSortedSlots(data) {
  const seen = new Set();
  return [...data]
    .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))
    .reduce((acc, d) => {
      if (d.time_slot_label && !seen.has(d.time_slot_label)) {
        seen.add(d.time_slot_label);
        acc.push(d.time_slot_label);
      }
      return acc;
    }, []);
}

// ── Exam helpers ─────────────────────────────────────────────────────────────
function groupExamsByDate(data) {
  const map = {};
  data.forEach((item) => {
    const dateKey = item.exam_date || 'Unknown';
    if (!map[dateKey]) map[dateKey] = { date: dateKey, dayLabel: '', sections: {} };
    if (item.exam_date && !map[dateKey].dayLabel) {
      const d = new Date(item.exam_date);
      map[dateKey].dayLabel = d.toLocaleDateString('en-GB', { weekday: 'long' });
    }
    const venue = item.room_name || 'Unassigned';
    if (!map[dateKey].sections[venue]) map[dateKey].sections[venue] = { morning: [], afternoon: [] };
    const slot = (item.time_slot_label || '').toLowerCase();
    const session = slot.includes('8') || slot.includes('morning') ? 'morning' : 'afternoon';
    map[dateKey].sections[venue][session].push(item);
  });
  return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
}

function fmtDate(raw) {
  return new Date(raw).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Search filter ────────────────────────────────────────────────────────────
function filterEntries(data, query) {
  if (!query.trim()) return data;
  const q = query.toLowerCase();
  return data.filter(e =>
    e.course_code?.toLowerCase().includes(q) ||
    e.course_name?.toLowerCase().includes(q) ||
    e.room_name?.toLowerCase().includes(q) ||
    e.lecturer_name?.toLowerCase().includes(q) ||
    e.day_of_week?.toLowerCase().includes(q) ||
    e.time_slot_label?.toLowerCase().includes(q)
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────
function CoursePill({ item, getColor }) {
  const c = getColor(item.course_code);
  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', gap: 2,
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 8, padding: '5px 8px',
      margin: '3px 4px 3px 0', minWidth: 100, maxWidth: 200,
    }}>
      {item.course_code && (
        <span style={{ fontSize: 9, fontWeight: 800, color: c.text, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {item.course_code}
        </span>
      )}
      <span style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
        {item.course_name || '—'}
      </span>
      {item.student_count && (
        <span style={{ fontSize: 10, color: '#64748B' }}>({item.student_count})</span>
      )}
    </div>
  );
}

function SessionCell({ items, getColor }) {
  if (!items || items.length === 0) {
    return <td style={tdStyle}><span style={{ color: '#CBD5E1', fontSize: 11 }}>—</span></td>;
  }
  return (
    <td style={tdStyle}>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {items.map((item, i) => <CoursePill key={item.id || i} item={item} getColor={getColor} />)}
      </div>
    </td>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const thStyle = {
  background: '#F8FAFC', border: '1px solid #E2E8F0',
  padding: '10px 14px', fontSize: 11, fontWeight: 700,
  color: '#534AB7', letterSpacing: '0.05em',
  textAlign: 'left', whiteSpace: 'nowrap',
};
const tdStyle = {
  border: '1px solid #E2E8F0', padding: '8px 10px',
  verticalAlign: 'top', fontSize: 12,
};
const dayTdStyle = {
  ...tdStyle, background: '#F8FAFC',
  minWidth: 100, verticalAlign: 'middle',
};
const venueTdStyle = {
  ...tdStyle, background: '#F8FAFC',
  minWidth: 90, verticalAlign: 'middle', textAlign: 'center',
};

// ── Main component ───────────────────────────────────────────────────────────
export default function TimetableView() {
  const [viewType, setViewType]   = useState('class');
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [filterDay, setFilterDay] = useState('');
  const getColor                  = useCourseColors();

  const classColorMap = useRef({});
  const classColorIdx = useRef(0);
  const getClassColor = (key) => {
    if (!key) return PILL_COLORS[0];
    if (!classColorMap.current[key]) {
      classColorMap.current[key] = PILL_COLORS[classColorIdx.current % PILL_COLORS.length];
      classColorIdx.current++;
    }
    return classColorMap.current[key];
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setSearch('');
    setFilterDay('');
    const ep = viewType === 'class' ? '/class-timetables' : '/exam-timetables';
    api.get(ep)
      .then(r => setData(r.data || []))
      .catch(e => { console.error(e); setError('Could not load timetable.'); setData([]); })
      .finally(() => setLoading(false));
  }, [viewType]);

  // ── Derived data ─────────────────────────────────────────────────────────
  // Time slots sorted correctly by start_time from DB
  const timeSlots = buildSortedSlots(data);

  // Apply day filter + search
  const visibleData = filterEntries(
    filterDay ? data.filter(e => e.day_of_week === filterDay) : data,
    search
  );

  const hasResults = visibleData.length > 0;

  // ── Class grid ─────────────────────────────────────────────────────────
  const buildClassGrid = (entries) => {
    const grid = {};
    const activeDays = filterDay ? [filterDay] : DAYS;
    activeDays.forEach(day => {
      grid[day] = {};
      timeSlots.forEach(slot => { grid[day][slot] = []; });
      entries.filter(d => d.day_of_week === day).forEach(d => {
        if (d.time_slot_label) {
          if (!grid[day][d.time_slot_label]) grid[day][d.time_slot_label] = [];
          grid[day][d.time_slot_label].push(d);
        }
      });
    });
    return grid;
  };

  const renderClassGrid = () => {
    if (!hasResults) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: '#CBD5E1', fontSize: 13, fontWeight: 600 }}>
          {search ? `No results for "${search}"` : 'No class schedule published yet'}
        </div>
      );
    }

    const grid = buildClassGrid(visibleData);
    const activeDays = filterDay ? [filterDay] : DAYS;

    return (
      <div style={{ overflowX: 'auto', borderRadius: 16, border: '1px solid #E2E8F0', boxShadow: '0 2px 16px rgba(83,74,183,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, color: '#CBD5E1', fontSize: 9, letterSpacing: '0.14em' }}>DAY / TIME</th>
              {timeSlots.map(slot => <th key={slot} style={thStyle}>{slot}</th>)}
            </tr>
          </thead>
          <tbody>
            {activeDays.map((day, di) => (
              <motion.tr
                key={day}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + di * 0.06 }}
              >
                <th style={{ ...dayTdStyle, textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {day.slice(0, 3)}
                  </div>
                  <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 2 }}>{day}</div>
                </th>
                {timeSlots.map(slot => {
                  const entries = grid[day]?.[slot] || [];
                  return (
                    <td key={slot} style={tdStyle}>
                      {entries.length === 0
                        ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 48 }}>
                            <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#E2E8F0' }} />
                          </div>
                        : entries.map((item, ii) => {
                          const c = getClassColor(item.course_code || item.course_name);
                          return (
                            <div key={item.id || ii} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '8px 10px', marginBottom: 6 }}>
                              {item.course_code && (
                                <div style={{ fontSize: 9, fontWeight: 800, color: c.text, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
                                  {item.course_code}
                                </div>
                              )}
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                                {item.course_name || '—'}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 10, color: '#64748B', marginTop: 4 }}>
                                {item.room_name && <span>📍 {item.room_name}</span>}
                                {item.lecturer_name && <span>👤 {item.lecturer_name}</span>}
                              </div>
                            </div>
                          );
                        })
                      }
                    </td>
                  );
                })}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Mobile class list (cards) ────────────────────────────────────────────
  const renderClassCards = () => {
    if (!hasResults) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: '#CBD5E1', fontSize: 13, fontWeight: 600 }}>
          {search ? `No results for "${search}"` : 'No class schedule published yet'}
        </div>
      );
    }

    // Group by day, preserving DB order (already sorted by day + start_time)
    const byDay = {};
    visibleData.forEach(e => {
      if (!byDay[e.day_of_week]) byDay[e.day_of_week] = [];
      byDay[e.day_of_week].push(e);
    });
    const activeDays = DAYS.filter(d => byDay[d]);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {activeDays.map(day => (
          <div key={day}>
            <div style={{
              fontSize: 11, fontWeight: 800, color: '#534AB7',
              textTransform: 'uppercase', letterSpacing: '0.1em',
              marginBottom: 10, paddingBottom: 6,
              borderBottom: '1px solid #E2E8F0',
            }}>
              {day}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byDay[day].map((item, i) => {
                const c = getClassColor(item.course_code || item.course_name);
                return (
                  <div key={item.id || i} style={{
                    background: c.bg, border: `1px solid ${c.border}`,
                    borderRadius: 12, padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        {item.course_code && (
                          <div style={{ fontSize: 9, fontWeight: 800, color: c.text, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
                            {item.course_code}
                          </div>
                        )}
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                          {item.course_name || '—'}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: c.text,
                        background: 'white', border: `1px solid ${c.border}`,
                        borderRadius: 6, padding: '3px 7px', whiteSpace: 'nowrap',
                      }}>
                        {item.time_slot_label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11, color: '#64748B' }}>
                      {item.room_name && <span>📍 {item.room_name}</span>}
                      {item.lecturer_name && <span>👤 {item.lecturer_name}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Exam grid ─────────────────────────────────────────────────────────────
  const renderExamGrid = () => {
    const grouped = groupExamsByDate(visibleData);
    if (grouped.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: '#CBD5E1', fontSize: 13, fontWeight: 600 }}>
          {search ? `No results for "${search}"` : 'No exams scheduled yet'}
        </div>
      );
    }
    return (
      <div style={{ overflowX: 'auto', borderRadius: 16, border: '1px solid #E2E8F0', boxShadow: '0 2px 16px rgba(83,74,183,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 140 }}>Day / Date</th>
              <th style={{ ...thStyle, width: 110 }}>Venue</th>
              <th style={thStyle}>Morning (8:00 AM – 11:00 AM)</th>
              <th style={thStyle}>Afternoon (2:00 PM – 5:00 PM)</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((dayGroup, di) => {
              const venues = Object.entries(dayGroup.sections);
              return venues.map(([venue, sessions], vi) => (
                <motion.tr
                  key={`${dayGroup.date}-${venue}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: di * 0.05 + vi * 0.02 }}
                >
                  {vi === 0 && (
                    <td rowSpan={venues.length} style={{ ...dayTdStyle, verticalAlign: 'middle', borderRight: '2px solid #E2E8F0' }}>
                      <div style={{ fontWeight: 800, fontSize: 12, color: '#534AB7', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Day {di + 1}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A', marginTop: 4 }}>
                        {dayGroup.dayLabel}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {fmtDate(dayGroup.date)}
                      </div>
                    </td>
                  )}
                  <td style={venueTdStyle}>
                    <span style={{
                      display: 'inline-block',
                      background: 'rgba(83,74,183,0.08)', border: '1px solid rgba(83,74,183,0.2)',
                      color: '#534AB7', borderRadius: 8, padding: '4px 8px',
                      fontSize: 10, fontWeight: 700, lineHeight: 1.4, textAlign: 'center',
                    }}>
                      {venue}
                    </span>
                  </td>
                  <SessionCell items={sessions.morning} getColor={getColor} />
                  <SessionCell items={sessions.afternoon} getColor={getColor} />
                </motion.tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const DAY_COLORS = {
    Monday:    { bg: '#EEEDFE', text: '#534AB7' },
    Tuesday:   { bg: '#E6F1FB', text: '#185FA5' },
    Wednesday: { bg: '#E1F5EE', text: '#0F6E56' },
    Thursday:  { bg: '#FAEEDA', text: '#854F0B' },
    Friday:    { bg: '#EAF3DE', text: '#3B6D11' },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        .tv-root { font-family: 'Plus Jakarta Sans', sans-serif; background: #FFFFFF; color: #0F172A; min-height: 100vh; padding: 120px 5% 0; display: flex; flex-direction: column; }
        .tv-inner { max-width: 1200px; margin: 0 auto; width: 100%; flex: 1; }
        .tv-topnav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 36px; flex-wrap: wrap; gap: 12px; }
        .tv-appname { display: inline-flex; align-items: center; gap: 12px; font-size: 20px; font-weight: 500; color: #0F172A; }
        .tv-nav-actions { display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .tv-home-btn { display: inline-flex; align-items: center; gap: 7px; background: #F8FAFC; color: #534AB7; border: 1px solid #E2E8F0; padding: 10px 18px; border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; text-decoration: none; transition: transform 0.18s, box-shadow 0.18s, background 0.18s; }
        .tv-home-btn:hover { background: #F1F0FF; transform: translateY(-2px); box-shadow: 0 4px 14px rgba(83,74,183,0.12); }
        .tv-download { display: inline-flex; align-items: center; gap: 8px; background: #534AB7; color: #fff; padding: 10px 18px; border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; text-decoration: none; box-shadow: 0 4px 16px rgba(83,74,183,0.3); transition: transform 0.18s, box-shadow 0.18s; }
        .tv-download:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(83,74,183,0.4); }
        .tv-toggle { display: inline-flex; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 14px; padding: 5px; gap: 4px; margin-bottom: 20px; }
        .tv-btn { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; padding: 10px 22px; border-radius: 10px; border: none; cursor: pointer; transition: all 0.2s; }
        .tv-btn.on { background: #534AB7; color: #fff; box-shadow: 0 4px 14px rgba(83,74,183,0.3); }
        .tv-btn.off { background: transparent; color: #94A3B8; }
        .tv-btn.off:hover { color: #534AB7; background: #F1F0FF; }
        .tv-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 24px; }
        .tv-search-wrap { position: relative; flex: 1; min-width: 200px; max-width: 360px; }
        .tv-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #CBD5E1; pointer-events: none; }
        .tv-search { width: 100%; padding: 10px 36px 10px 36px; border: 1px solid #E2E8F0; border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; color: #0F172A; background: #F8FAFC; outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
        .tv-search:focus { border-color: #534AB7; box-shadow: 0 0 0 3px rgba(83,74,183,0.1); background: #fff; }
        .tv-search::placeholder { color: #CBD5E1; }
        .tv-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #CBD5E1; padding: 2px; display: flex; align-items: center; }
        .tv-search-clear:hover { color: #534AB7; }
        .tv-day-pills { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .tv-day-pill { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11px; font-weight: 700; padding: 6px 12px; border-radius: 8px; border: 1px solid #E2E8F0; cursor: pointer; transition: all 0.15s; background: white; color: #64748B; }
        .tv-day-pill:hover { border-color: #C7D2FE; }
        .tv-loading { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 80px 0; }
        .spin { width: 28px; height: 28px; border: 2.5px solid #E0E7FF; border-top-color: #534AB7; border-radius: 50%; animation: rot 0.65s linear infinite; }
        @keyframes rot { to { transform: rotate(360deg); } }
        .spin-txt { font-size: 11px; font-weight: 700; color: #CBD5E1; letter-spacing: 0.12em; text-transform: uppercase; }
        .tv-err { background: #FFF1F2; border: 1px solid #FECDD3; border-radius: 14px; padding: 16px 20px; color: #E11D48; font-size: 13px; font-weight: 600; text-align: center; }
        .tv-footer { padding: 40px 5%; border-top: 1px solid #F1F5F9; text-align: center; color: #94A3B8; font-size: 12px; margin-top: 40px; }
        .tv-footer p { margin: 0; letter-spacing: 0.05em; text-transform: uppercase; }
        .tv-footer .sub { margin: 4px 0 0; display: flex; align-items: center; justify-content: center; gap: 4px; text-transform: none; letter-spacing: normal; }
        /* Mobile: show cards, hide grid table */
        .class-table-view { display: block; }
        .class-card-view  { display: none; }
        @media (max-width: 768px) {
          .tv-root { padding: 60px 4% 0; }
          .tv-topnav { flex-direction: column; align-items: flex-start; }
          .tv-nav-actions { width: 100%; }
          .tv-search-wrap { max-width: 100%; }
          .tv-btn { font-size: 12px; padding: 8px 16px; }
          .class-table-view { display: none; }
          .class-card-view  { display: block; }
        }
      `}</style>

      <div className="tv-root">
        <div className="tv-inner">

          {/* Top nav */}
          <motion.div className="tv-topnav" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="tv-appname">
              <img src="/unilia_logo.png" alt="Unilia Logo" style={{ height: 32, width: 'auto' }} />
              <span style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#003366' }}>
                MyTimetable
              </span>
            </div>
            <div className="tv-nav-actions">
              <a href="/" className="tv-home-btn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/>
                </svg>
                Home
              </a>
              <a href="/Mytimetable.apk" download className="tv-download">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download App
              </a>
            </div>
          </motion.div>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <motion.span
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                style={{ width: 5, height: 5, background: '#00D26A', borderRadius: '50%' }}
              />
              <span style={{ color: '#64748B', fontSize: 12, fontWeight: 500 }}>
                "Too cloudy? Clear the fog with a timetable built just for you."
              </span>
            </div>
            <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 600, color: '#0F172A' }}>Personalised version:</span>
              <a href="/Mytimetable.apk" download style={{ color: '#534AB7', fontWeight: 800, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Download APK
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </a>
            </div>
          </motion.div>

          {/* View toggle */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="tv-toggle">
              {[['class', 'Class Schedule'], ['exam', 'Exam Schedule']].map(([v, label]) => (
                <button key={v} className={`tv-btn ${viewType === v ? 'on' : 'off'}`} onClick={() => setViewType(v)}>
                  {label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Search + day filter toolbar */}
          {!loading && !error && (
            <motion.div className="tv-toolbar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
              {/* Search */}
              <div className="tv-search-wrap">
                <span className="tv-search-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  className="tv-search"
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={viewType === 'class' ? 'Search course, room, lecturer…' : 'Search course, venue…'}
                />
                {search && (
                  <button className="tv-search-clear" onClick={() => setSearch('')}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>

              {/* Day filter pills — class view only */}
              {viewType === 'class' && (
                <div className="tv-day-pills">
                  <button
                    className="tv-day-pill"
                    style={!filterDay ? { background: '#0F172A', color: '#fff', borderColor: '#0F172A' } : {}}
                    onClick={() => setFilterDay('')}
                  >
                    All
                  </button>
                  {DAYS.map(d => {
                    const dc = DAY_COLORS[d] || { bg: '#F1F5F9', text: '#64748B' };
                    const active = filterDay === d;
                    return (
                      <button
                        key={d}
                        className="tv-day-pill"
                        style={active ? { background: dc.bg, color: dc.text, borderColor: dc.text + '40' } : {}}
                        onClick={() => setFilterDay(filterDay === d ? '' : d)}
                      >
                        {d.slice(0, 3)}
                      </button>
                    );
                  })}
                  {(search || filterDay) && (
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 4 }}>
                      {visibleData.length} result{visibleData.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Loading */}
          {loading && (
            <div className="tv-loading">
              <div className="spin" />
              <span className="spin-txt">Loading…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && <div className="tv-err">⚠ {error}</div>}

          <AnimatePresence mode="wait">
            {!loading && !error && viewType === 'class' && (
              <motion.div key="class" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                {/* Desktop: grid table */}
                <div className="class-table-view">{renderClassGrid()}</div>
                {/* Mobile: stacked cards */}
                <div className="class-card-view">{renderClassCards()}</div>
              </motion.div>
            )}

            {!loading && !error && viewType === 'exam' && (
              <motion.div key="exam" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                {renderExamGrid()}
              </motion.div>
            )}
          </AnimatePresence>

        </div>

        <footer className="tv-footer">
          <p>© 2026 Panjie.Dev</p>
          <p className="sub">Built with <span style={{ color: '#EF4444' }}>❤️</span> from Panjie.Dev</p>
        </footer>
      </div>
    </>
  );
}